import { CommonModule } from '@angular/common'
import { Component, inject, NgZone, OnDestroy, OnInit, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { LoggerService } from '@danangelus/ngx-app-core'
import { ButtonModule } from 'primeng/button'
import { InputGroupModule } from 'primeng/inputgroup'
import { InputGroupAddonModule } from 'primeng/inputgroupaddon'
import { InputOtpModule } from 'primeng/inputotp'
import { InputTextModule } from 'primeng/inputtext'
import { MessageModule } from 'primeng/message'
import { PaginatorModule } from 'primeng/paginator'
import { ProgressSpinner } from 'primeng/progressspinner'
import { SessionService } from '../../../core/security/session.service'
import { LdapManagerService } from '../../../core/services/ldap-manager.service'
import { SsoService } from '../../../core/services/sso.service'
import { MfaForm } from '../../../models/form.model'
import { GatewayError } from '../../../models/gateway.model'
import { User } from "../../../models/user.model"

const log: LoggerService = LoggerService.get('MfaComponent')

/**
 * Multi-Factor Authentication Component responsible for sending and validating user Code
 *
 * @author Adam Jennings
 */
@Component({
    selector: 'mfa',
    standalone: true,
    imports: [
        ButtonModule,
        CommonModule,
        FormsModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputOtpModule,
        InputTextModule,
        MessageModule,
        PaginatorModule,
        ProgressSpinner
    ],
    templateUrl: './mfa.component.html',
    styleUrl: './mfa.component.sass'
})
export class MfaComponent implements OnInit, OnDestroy {

    private ldapManager = inject(LdapManagerService)
    private ssoService = inject(SsoService)
    private router = inject(Router)
    private sessionService = inject(SessionService)
    private ngZone = inject(NgZone)

    form: MfaForm | undefined
    mfaOption: string | undefined
    loading = signal(false)
    showResend = signal(false)
    sendingCode = signal(false)
    private resendTimer: ReturnType<typeof setTimeout> | undefined

    async ngOnInit(): Promise<void> {
        this.loading.set(true)
        this.form = { code: '', errors: {} }

        const user = await this.sessionService.getUser()

        const response = await this.ldapManager.getDefaultMfaOption(user!.userId!)
        if (response instanceof GatewayError) {
            log.debug(`ngOnInit`, `Error getting default MFA option: ${response}`)
            this.form!.errors.code = 'FAILED_TO_SEND'
        } else {
            this.mfaOption = response
            if (this.mfaOption !== 'totp') {
                this.startResendTimer()
            }
        }
        this.loading.set(false)
    }

    ngOnDestroy(): void {
        if (this.resendTimer) {
            clearTimeout(this.resendTimer)
        }
    }

    private startResendTimer(): void {
        this.showResend.set(false)
        this.resendTimer = setTimeout(() => {
            this.ngZone.run(() => this.showResend.set(true))
        }, 30000)
    }

    async login(): Promise<void> {
        log.debug('login', `Validating code...${this.form?.code}`)
        if (!this.form!.code && this.form!.code!.length !== 6) {
            this.invalidCode()
            return
        }
        try {
            const type = (this.mfaOption || 'email') as 'email' | 'sms' | 'totp';
            const user = await this.ssoService.checkMfaCode(this.form!.code!, type, false);
            if (user instanceof User && user?.verified) {
                log.debug('login', 'MfaCode correct. Navigating to homepage')
                this.sessionService.userVerified(new User(user!))
                this.ngZone.run(() => this.router.navigate(['home']))
            } else {
                log.debug('login', 'Invalid MfaCode, throwing error...')
                this.invalidCode()
            }
        } catch (e) {
            log.error(`Error validating code: ${e}`)
            this.invalidCode()
        }
    }

    async sendCode(): Promise<void> {
        try {
            this.form!.errors.code = ''
            this.showResend.set(false)
            this.sendingCode.set(true)
            await this.ssoService.sendMfa()
            this.sendingCode.set(false)
            this.startResendTimer()
        } catch (error) {
            log.debug('sendCode', `Error sending MFA code: ${error}`)
            this.sendingCode.set(false)
            this.form!.errors.code = 'FAILED_TO_SEND'
            this.showResend.set(true)
        }
    }

    invalidCode(): void {
        this.form!.errors.code = 'INVALID_CODE'
    }
}
